// src/pages/auth/ResetPasswordPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, CheckCircle, AlertCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { PasswordInput } from '@/components/ui/PasswordInput';
import { verifyPasswordResetToken, confirmPasswordReset } from '@/services/apiService';
import AuthLayout from '@/layouts/AuthLayout';

export const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get('token');
  const email = searchParams.get('email');

  const [password, setPassword]               = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [verifying, setVerifying]             = useState(true);
  const [tokenValid, setTokenValid]           = useState(false);
  const [loading, setLoading]                 = useState(false);
  const [error, setError]                     = useState('');
  const [fieldErrors, setFieldErrors]         = useState({});
  const [success, setSuccess]                 = useState(false);

  useEffect(() => {
    const verifyToken = async () => {
      if (!token || !email) {
        setError('Invalid reset link. Please request a new password reset.');
        setVerifying(false);
        return;
      }
      try {
        await verifyPasswordResetToken(email, token);
        setTokenValid(true);
      } catch (err) {
        setError(
          err?.response?.data?.error ||
          'This reset link is invalid or has expired. Please request a new one.'
        );
      } finally {
        setVerifying(false);
      }
    };
    verifyToken();
  }, [token, email]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setFieldErrors({});

    if (password !== confirmPassword) {
      setFieldErrors({ confirmPassword: 'Passwords do not match' });
      return;
    }
    if (password.length < 8) {
      setFieldErrors({ password: 'Password must be at least 8 characters' });
      return;
    }

    setLoading(true);
    try {
      await confirmPasswordReset(email, token, password);
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      const backendErrors = err?.response?.data?.errors || {};
      if (Object.keys(backendErrors).length > 0) {
        setFieldErrors(backendErrors);
        setError('Please fix the errors below.');
      } else {
        setError(err?.response?.data?.error || 'Failed to reset password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Verifying ────────────────────────────────────────────────────────────
  if (verifying) {
    return (
      <AuthLayout heading="Reset Password">
        <div className="flex flex-col items-center gap-4 py-8">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-mikado-yellow" />
          <p className="text-swan/60 text-sm">Verifying reset link...</p>
        </div>
      </AuthLayout>
    );
  }

  // ── Invalid token ─────────────────────────────────────────────────────────
  if (!tokenValid) {
    return (
      <AuthLayout heading="Invalid Link">
        <div className="flex flex-col items-center gap-4 py-4 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-red-500/10 rounded-full">
            <AlertCircle className="text-red-500" size={28} />
          </div>
          <p className="text-swan/70 text-sm">
            {error || 'This password reset link is invalid or has expired.'}
          </p>
          <Button onClick={() => navigate('/login')} className="w-full mt-2">
            Back to Login
          </Button>
        </div>
      </AuthLayout>
    );
  }

  // ── Success ───────────────────────────────────────────────────────────────
  if (success) {
    return (
      <AuthLayout heading="Password Reset">
        <div className="flex flex-col items-center gap-4 py-4 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-green-500/10 rounded-full">
            <CheckCircle className="text-green-500" size={28} />
          </div>
          <p className="text-swan/70 text-sm">
            Your password has been updated. Redirecting to login...
          </p>
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-mikado-yellow mt-2" />
        </div>
      </AuthLayout>
    );
  }

  // ── Form ──────────────────────────────────────────────────────────────────
  return (
    <AuthLayout
      heading="Create New Password"
      subheading="Enter a strong password for your account"
    >
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="flex flex-col gap-5"
      >
        {error && (
          <div className="flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
            <AlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={15} />
            <p className="text-sm text-red-400">{error}</p>
          </div>
        )}

        {/* New password */}
        <div>
          <PasswordInput
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="New password"
            required
            disabled={loading}
            className={fieldErrors.password ? 'border-red-500' : ''}
          />
          {fieldErrors.password && (
            <p className="text-xs text-red-500 mt-1">{fieldErrors.password}</p>
          )}
        </div>

        {/* Confirm password */}
        <div>
          <PasswordInput
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            placeholder="Confirm new password"
            required
            disabled={loading}
            className={fieldErrors.confirmPassword ? 'border-red-500' : ''}
          />
          {fieldErrors.confirmPassword && (
            <p className="text-xs text-red-500 mt-1">{fieldErrors.confirmPassword}</p>
          )}
        </div>

        {/* Requirements */}
        <div className="bg-white/5 rounded-lg p-3">
          <p className="text-xs font-medium text-swan/60 mb-2">Requirements</p>
          <ul className="text-xs space-y-1">
            <li className={password.length >= 8 ? 'text-green-400' : 'text-swan/40'}>• At least 8 characters</li>
            <li className={/[A-Z]/.test(password) ? 'text-green-400' : 'text-swan/40'}>• Uppercase letter</li>
            <li className={/[a-z]/.test(password) ? 'text-green-400' : 'text-swan/40'}>• Lowercase letter</li>
            <li className={/[0-9]/.test(password) ? 'text-green-400' : 'text-swan/40'}>• Number</li>
          </ul>
        </div>

        <Button
          type="button"
          onClick={handleSubmit}
          disabled={loading || !password || !confirmPassword}
          className="w-full"
        >
          {loading ? 'Resetting...' : 'Reset Password'}
        </Button>

        <button
          type="button"
          onClick={() => navigate('/login')}
          className="text-sm text-swan/40 hover:text-swan/70 transition-colors text-center"
        >
          Back to Login
        </button>
      </motion.div>
    </AuthLayout>
  );
};